import React, { createContext, useContext, useMemo } from "react";

import { useDashboardWorkloads } from "../../hooks";
import { type DashboardWorkload } from "../../types";
import { TimeRange } from "./Components/TimeRangeSelector";

export type ClusterDetailDashboardContextType = {
  dashboardWorkloads: DashboardWorkload[];
  loading: boolean;
  timeRange?: TimeRange;
};

export const ClusterDetailDashboardContext = createContext<ClusterDetailDashboardContextType>({
  dashboardWorkloads: [],
  loading: true,
  timeRange: TimeRange.T_30D,
});

interface ClusterDetailDashboardContextProviderProps {
  timeRange?: TimeRange;
  clusterId: string;
  children: React.ReactNode;
}

export const ClusterDetailDashboardContextProvider = ({
  children,
  clusterId,
  timeRange,
}: ClusterDetailDashboardContextProviderProps) => {
  const { dashboardWorkloads, loading } = useDashboardWorkloads(clusterId, timeRange);

  const value: ClusterDetailDashboardContextType = useMemo(
    () => ({
      dashboardWorkloads,
      loading,
      timeRange,
    }),
    [dashboardWorkloads, loading, timeRange]
  );

  return <ClusterDetailDashboardContext.Provider value={value}>{children}</ClusterDetailDashboardContext.Provider>;
};

export const useClusterDetailDashboardContext = () => {
  const context = useContext(ClusterDetailDashboardContext);

  if (!context) {
    throw new Error("useClusterDetailDashboardContext must be used within a ClusterDetailDashboardContextProvider");
  }

  return context;
};
